import {ref, computed} from 'vue';
import {defineStore} from 'pinia';
import {useLogger, useTranslation, useFormat} from '#imports';
import {useErrorHandler} from '@malt/utils';
import {pushVJoySnackbar} from '@maltjoy/core-vue';
import {FetchError} from '@api';
import type {
    ProjectInformationResource,
    ProjectDeliverableResource,
    ProjectLotDeliverableResource,
    TimeAndMaterialsProjectDetailsResource,
    FixedPriceProjectDetailsResource,
    ProjectContractResource,
    ContractContextResource,
    UpdateContractRequest,
    Currency,
    ProjectBudgetResource,
    ContractTermsResource,
    AcceptSubcontractingContractTermsRequest,
    AcceptTripartiteContractTermsRequest,
    ContractSubcontractingModeResource,
    ContractTripartiteModeResource,
    ValidationError,
} from '@api';
import {useActivityStore} from './activity.store';
import type {FormattedPaymentsTableItem} from '../types';
import type {TranslationKey} from '#build/i18n';
import {projectEndpoints, projectTermsEndpoints} from '@/registry';

const COMPLETE_PROJECT_ERROR_MESSAGE_KEYS: Record<string, TranslationKey | undefined> = {
    PendingContractAmendment: 'projectfreelancer.projectview.layout.project-actions.complete-project.error.pending-contract-amendment',
    MissionValidationRejected: 'projectfreelancer.projectview.layout.project-actions.complete-project.error.mission-validation-rejected',
};

export const useProjectInformationStore = defineStore('freelancer-project-information', () => {
    const logger = useLogger();
    const {handleUnknownError} = useErrorHandler();

    const {t} = useTranslation();
    const {formatAmount, formatDate, formatDateToShort} = useFormat();

    const projectInformation = ref<ProjectInformationResource>(null as unknown as ProjectInformationResource);
    const projectDeliverables = ref<ProjectDeliverableResource[] | ProjectLotDeliverableResource[]>();
    const projectBudget = ref<ProjectBudgetResource>();
    const timeBasedDetails = ref<TimeAndMaterialsProjectDetailsResource>();
    const taskBasedDetails = ref<FixedPriceProjectDetailsResource>();
    const contractInformation = ref<ProjectContractResource>(null as unknown as ProjectContractResource);
    const contractContext = ref<ContractContextResource | null>(null);
    const contractTerms = ref<ContractTermsResource>(null as unknown as ContractTermsResource);

    const isProjectTimeBased = computed(() => projectInformation.value?.contract.type === 'TIME_AND_MATERIALS');
    const isProjectTaskBased = computed(() => projectInformation.value?.contract.type === 'FIXED_PRICE');

    // loading states
    const isCompleteProjectLoading = ref(false);
    const isCancelProjectLoading = ref(false);
    const pendingProjectDeliverables = ref(false);
    const isDismissProjectRenewalLoading = ref(false);
    const isCancelContractLoading = ref(false);

    // activity store
    const activityStore = useActivityStore();

    async function getProjectInformation(projectId: string) {
        try {
            projectInformation.value = await projectEndpoints.getProjectInformation({projectId});
            if (isProjectTimeBased.value) {
                timeBasedDetails.value = projectInformation.value.details as TimeAndMaterialsProjectDetailsResource;
                taskBasedDetails.value = null as unknown as FixedPriceProjectDetailsResource;
            } else if (isProjectTaskBased.value) {
                taskBasedDetails.value = projectInformation.value.details as FixedPriceProjectDetailsResource;
                timeBasedDetails.value = null as unknown as TimeAndMaterialsProjectDetailsResource;
            }
        } catch (e) {
            handleUnknownError(e, {showSnackbar: false});
        }
    }

    async function fetchProjectInformationByCorrelationId(correlationId: string) {
        try {
            projectInformation.value = await projectEndpoints.getProjectInformationByCorrelation({
                correlationId,
            });
        } catch (e) {
            logger.error(e);
            return Promise.reject(e);
        }
    }

    async function fetchTerms(projectId: string) {
        try {
            contractTerms.value = await projectTermsEndpoints.getTerms({projectId});
        } catch (e) {
            logger.error(e);
            return Promise.reject(e);
        }
    }

    function buildAcceptContractTermsRequestFromStore(): AcceptTripartiteContractTermsRequest | AcceptSubcontractingContractTermsRequest | undefined {
        const pending = contractTerms.value?.pending;

        if (pending?.type === 'TRIPARTITE') {
            const projectTermsId = (pending as ContractTripartiteModeResource).projectTerms?.id;
            if (!projectTermsId) {
                return undefined;
            }
            return {invoicingMode: 'TRIPARTITE', projectTermsId};
        }

        if (pending?.type === 'SUBCONTRACTING') {
            const clientTermsId = (pending as ContractSubcontractingModeResource).clientTerms?.id;
            if (!clientTermsId) {
                return undefined;
            }
            return {invoicingMode: 'SUBCONTRACTING', clientTermsId};
        }

        return undefined;
    }

    async function updateTerms(projectId: string) {
        const acceptContractTermsRequest = buildAcceptContractTermsRequestFromStore();
        if (!acceptContractTermsRequest) {
            pushVJoySnackbar({
                props: {
                    message: t('projectfreelancer.projectview.contract-amendment.notification.submit.request-incomplete.message'),
                    level: 'error',
                },
            });
            return Promise.reject(new Error('Incomplete acceptation contract terms request'));
        }
        try {
            await projectTermsEndpoints.acceptContractTerms({projectId, acceptContractTermsRequest});
            await fetchTerms(projectId);
            await activityStore.fetchProjectActivity(projectId);
            pushVJoySnackbar({
                props: {
                    header: t('projectfreelancer.projectview.contract-amendment.notification.submit.success.header'),
                    message: t('projectfreelancer.projectview.contract-amendment.notification.submit.success.message'),
                    level: 'success',
                },
            });
        } catch (e) {
            handleUnknownError(e, {showSnackbar: true});
            return Promise.reject(e);
        }
    }

    async function getProjectDeliverables(projectId: string) {
        try {
            pendingProjectDeliverables.value = true;
            projectDeliverables.value = await projectEndpoints.getProjectDeliverables({projectId});
        } catch (e) {
            handleUnknownError(e, {showSnackbar: false});
        } finally {
            pendingProjectDeliverables.value = false;
        }
    }

    async function getProjectBudget(projectId: string) {
        try {
            projectBudget.value = await projectEndpoints.getProjectBudget({projectId});
        } catch (e) {
            handleUnknownError(e, {showSnackbar: false});
        }
    }

    async function updateBillingReference(projectId: string, reference: string) {
        try {
            await projectEndpoints.updateProjectFreelancerExternalReference({
                projectId,
                updateProjectFreelancerExternalReferenceRequest: {reference},
            });
            if (projectInformation.value) {
                projectInformation.value.billingReference = reference;
            }
        } catch (e) {
            handleUnknownError(e, {showSnackbar: false});
        }
    }

    async function timeTravelProject(newDate: Date) {
        try {
            await projectEndpoints.updateClockOffsetDateTime({
                projectId: projectInformation.value.projectId,
                updateClockOffsetDateTimeRequest: {newDate},
            });
            await getProjectInformation(projectInformation.value.projectId);
        } catch (e) {
            return Promise.reject(e);
        }
    }

    async function cancelProject(projectId: string, adminComment?: string) {
        try {
            isCancelProjectLoading.value = true;
            await projectEndpoints.cancelProject({
                projectId,
                cancelProjectRequest: adminComment ? {adminComment} : undefined,
            });
            pushVJoySnackbar({
                props: {
                    header: t('projectfreelancer.projectview.layout.project-actions.cancel-project.notification.header'),
                    message: t('projectfreelancer.projectview.layout.project-actions.cancel-project.notification.message', [
                        projectInformation.value?.client.fullName,
                    ]),
                    level: 'success',
                },
            });
            await getProjectInformation(projectId);
            await activityStore.fetchProjectActivity(projectId);
        } catch (e) {
            handleUnknownError(e, {showSnackbar: true});
        } finally {
            isCancelProjectLoading.value = false;
        }
    }

    async function completeProject(projectId: string, adminComment?: string) {
        try {
            isCompleteProjectLoading.value = true;
            await projectEndpoints.validateProject({
                projectId,
                validateProjectRequest: adminComment ? {adminComment} : undefined,
            });
            pushVJoySnackbar({
                props: {
                    header: t('projectfreelancer.projectview.layout.project-actions.complete-project.notication.header'),
                    message: t('projectfreelancer.projectview.layout.project-actions.complete-project.notication.message', [
                        projectInformation.value?.client.fullName,
                    ]),
                    level: 'success',
                },
            });
            await getProjectInformation(projectId);
            await activityStore.fetchProjectActivity(projectId);
        } catch (e) {
            if (e instanceof FetchError) {
                const causeData = e.getCauseData() as ValidationError[] | undefined;
                const messageKey = Array.isArray(causeData)
                    ? causeData.map(({error}) => (error ? COMPLETE_PROJECT_ERROR_MESSAGE_KEYS[error] : undefined)).find(Boolean)
                    : undefined;
                if (messageKey) {
                    pushVJoySnackbar({
                        props: {
                            message: t(messageKey),
                            level: 'error',
                        },
                    });
                    return;
                }
            }
            handleUnknownError(e, {showSnackbar: true});
        } finally {
            isCompleteProjectLoading.value = false;
        }
    }

    async function dismissProjectRenewal(projectId: string) {
        try {
            isDismissProjectRenewalLoading.value = true;
            await projectEndpoints.acknowledgeProjectFreelancerItem({
                projectId,
                acknowledgeProjectFreelancerItemRequest: {
                    type: 'PROJECT_RENEWAL_DISMISSED',
                },
            });
        } catch (e) {
            handleUnknownError(e, {showSnackbar: true});
            throw e;
        } finally {
            isDismissProjectRenewalLoading.value = false;
            await activityStore.fetchProjectActivity(projectId);
        }
    }

    async function getContractInformation(projectId: string) {
        try {
            contractInformation.value = await projectEndpoints.getProjectContract({projectId});
        } catch (e) {
            handleUnknownError(e, {showSnackbar: true});
            throw e;
        }
    }

    async function getContractContext(projectId: string) {
        try {
            contractContext.value = await projectEndpoints.fetchContractContext({projectId});
        } catch (e) {
            handleUnknownError(e, {showSnackbar: false});
            contractContext.value = null;
        }
    }

    async function validateContractAmendment(projectId: string, updateContractRequest: UpdateContractRequest) {
        try {
            await projectEndpoints.amendContract({projectId, updateContractRequest});
            await activityStore.fetchProjectActivity(projectId);
            pushVJoySnackbar({
                props: {
                    header: t('projectfreelancer.projectview.actions.card.review-contract-changes.notification.submit.header'),
                    message: t('projectfreelancer.projectview.actions.card.review-contract-changes.notification.submit.message', [
                        projectInformation.value?.client.fullName,
                    ]),
                    level: 'success',
                },
            });
        } catch (e) {
            handleUnknownError(e, {showSnackbar: true});
            throw e;
        }
    }

    async function cancelContractAmendment(projectId: string, amendmentId: string, cancelAmendmentRequest: {adminComment?: string}) {
        try {
            isCancelContractLoading.value = true;
            await projectEndpoints.cancelContractAmendment({projectId, amendmentId, cancelAmendmentRequest});
            await activityStore.fetchProjectActivity(projectId);
            pushVJoySnackbar({
                props: {
                    message: t('projectfreelancer.projectview.actions.card.review-contract-changes.notification.cancel.message'),
                    level: 'success',
                },
            });
        } catch (e) {
            handleUnknownError(e, {showSnackbar: false});
            throw e;
        } finally {
            isCancelContractLoading.value = false;
        }
    }

    function formatAmountWithDecimals(amount: number | undefined, currency: Currency, decimals = 2) {
        return amount && !isNaN(amount) ? formatAmount(currency, amount, {decimals}) : '-';
    }

    function isItemLot(item: ProjectDeliverableResource | ProjectLotDeliverableResource): item is ProjectLotDeliverableResource {
        return item.type === 'LOT';
    }

    function formatDisplayDate(item: ProjectDeliverableResource | ProjectLotDeliverableResource) {
        if (item.payment.status === 'COMPLETED_PAYMENT') {
            const paymentDate = (item.payment as {paymentDate?: string}).paymentDate;
            if (paymentDate) {
                return formatDateToShort(paymentDate);
            }
        }
        return '-';
    }

    const notificationByDeliverableId = computed(
        () => new Map(activityStore.paymentNotifications.map((notification) => [notification.deliverableId, notification.hasNotification])),
    );

    const deliverableNotificationCount = computed(
        () => activityStore.paymentNotifications.filter((notification) => notification.hasNotification).length,
    );

    async function acknowledgeProjectFreelancerItem(projectId: string, lotId?: string) {
        try {
            await projectEndpoints.acknowledgeProjectFreelancerItem({
                projectId,
                acknowledgeProjectFreelancerItemRequest: {
                    type: 'PAYMENT_STATUS_VIEWED',
                    lotId,
                },
            });
            await activityStore.fetchPaymentNotifications(projectId);
        } catch (e) {
            handleUnknownError(e, {showSnackbar: false});
        }
    }

    const formattedDeliverables = computed<FormattedPaymentsTableItem[]>(() => {
        return (
            projectDeliverables.value
                ?.filter((item) => item.payment.status !== 'AWAITING_DELIVERABLE_VALIDATION')
                .map((item: ProjectDeliverableResource | ProjectLotDeliverableResource) => {
                    const isLot = isItemLot(item);
                    const formattedItem = {
                        title: item.type,
                        status: item.payment.status,
                        amount: formatAmountWithDecimals(item.payment.amountInclTaxes, item.payment.currency),
                        currency: item.payment.currency,
                        date: formatDisplayDate(item), // transferDate in table, the date payment was made
                        completionDate: item.completionDate ? formatDate(item.completionDate) : '-',
                        invoices: item.invoices,
                        type: item.type,
                        month: isLot ? item.month : undefined,
                        workAmount: isLot ? item.workAmount : undefined,
                        workedUnit: isLot ? item.workedUnit : undefined,
                        payment: item.payment,
                        hasNotification: notificationByDeliverableId.value.get(item.id) ?? false,
                    };
                    return {
                        ...formattedItem,
                        id: item.id,
                    } satisfies FormattedPaymentsTableItem;
                }) ?? []
        );
    });

    return {
        // PROJECT INFORMATION
        projectInformation,
        getProjectInformation,
        fetchProjectInformationByCorrelationId,
        fetchTerms,
        updateTerms,
        contractTerms,
        isProjectTaskBased,
        timeBasedDetails,
        taskBasedDetails,
        // PROJECT ACTIONS
        updateBillingReference,
        timeTravelProject,
        cancelProject,
        completeProject,
        isCompleteProjectLoading,
        isCancelProjectLoading,
        // PROJECT DELIVERABLES
        getProjectDeliverables,
        formattedDeliverables,
        pendingProjectDeliverables,
        deliverableNotificationCount,
        acknowledgeProjectFreelancerItem,
        // PROJECT BUDGET
        getProjectBudget,
        projectBudget,
        // PROJECT RENEWAL
        dismissProjectRenewal,
        isDismissProjectRenewalLoading,
        getContractInformation,
        getContractContext,
        validateContractAmendment,
        cancelContractAmendment,
        contractInformation,
        contractContext,
        isCancelContractLoading,
    };
});
